import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { PixPayment } from "./PixPayment";
import { ProrationCardPayment } from "./ProrationCardPayment";

interface ChangePlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlanId: string | null;
}

export function ChangePlanModal({ open, onOpenChange, currentPlanId }: ChangePlanModalProps) {
  const { tenantId } = useTenant();
  const [plans, setPlans] = useState<any[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [quote, setQuote] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [payNow, setPayNow] = useState(false);
  const [payMethod, setPayMethod] = useState<'pix'|'card'>('pix');

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from("subscription_plans").select("id, name, price, status").eq("status", "active");
      setPlans(data || []);
      setSelected(null);
      setQuote(null);
    };
    if (open) load();
  }, [open]);

  useEffect(() => {
    const doQuote = async () => {
      if (!open || !tenantId || !currentPlanId || !selected || selected === currentPlanId) { setQuote(null); return; }
      const { data } = await supabase.functions.invoke('subscription_changes', {
        body: { action: 'quote_change', tenant_id: tenantId, from_plan_id: currentPlanId, to_plan_id: selected }
      });
      setQuote(data || null);
    };
    doQuote();
  }, [open, tenantId, currentPlanId, selected]);

  const canApplyNow = useMemo(() => selected && currentPlanId && selected !== currentPlanId, [selected, currentPlanId]);

  const applyNow = async () => {
    if (!tenantId || !currentPlanId || !selected) return;
    if ((quote?.quoted_amount || 0) > 0) {
      setPayNow(true);
    } else {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        const { data, error } = await supabase.functions.invoke('subscription_changes', {
          body: { action: 'apply_upgrade_now', tenant_id: tenantId, from_plan_id: currentPlanId, to_plan_id: selected, user_id: userId, already_paid: true }
        });
        if (error || !data?.success) throw new Error(data?.error || 'Falha ao aplicar upgrade');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const scheduleChange = async () => {
    if (!tenantId || !selected) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('subscription_changes', {
        body: { action: 'schedule_change', tenant_id: tenantId, to_plan_id: selected }
      });
      if (error || !data?.success) throw new Error(data?.error || 'Falha ao agendar troca');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Mudar Plano</DialogTitle>
          <DialogDescription>Escolha o novo plano e como aplicar</DialogDescription>
        </DialogHeader>

        {!payNow && (
        <div className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {plans.map((p) => (
              <button key={p.id} className={`p-3 border rounded ${selected===p.id? 'border-primary bg-primary text-primary-foreground':'border-border'}`} onClick={()=> setSelected(p.id)}>
                <div className="text-sm font-semibold">{p.name}</div>
                <div className="text-xs">{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(p.price)}/mês</div>
              </button>
            ))}
          </div>
          {quote && (
            <div className="p-3 border rounded text-sm">
              <div>Diferença proporcional agora: <strong>{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(quote.quoted_amount||0)}</strong></div>
              <div>Troca efetiva no próximo ciclo: <strong>{new Date(quote.effective_date).toLocaleString('pt-BR')}</strong></div>
            </div>
          )}
        </div>
        )}

        {!payNow ? (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={()=> onOpenChange(false)} disabled={loading}>Cancelar</Button>
            <Button onClick={scheduleChange} disabled={!canApplyNow || loading}>Agendar Troca</Button>
            <Button onClick={applyNow} disabled={!canApplyNow || loading}>
              {loading? 'Aplicando...' : 'Aplicar Agora'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <DialogDescription>Pague a diferença proporcional agora para ativar imediatamente o novo plano.</DialogDescription>
            <div className="flex gap-2">
              <Button variant={payMethod==='pix'?'default':'outline'} onClick={()=> setPayMethod('pix')}>PIX</Button>
              <Button variant={payMethod==='card'?'default':'outline'} onClick={()=> setPayMethod('card')}>Cartão</Button>
            </div>
            {payMethod === 'pix' ? (
              <PixPayment
                onSuccess={async ()=>{
                  const { data: { session } } = await supabase.auth.getSession();
                  const userId = session?.user?.id;
                  await supabase.functions.invoke('subscription_changes', {
                    body: { action: 'apply_upgrade_now', tenant_id: tenantId, from_plan_id: currentPlanId, to_plan_id: selected, user_id: userId, already_paid: true }
                  });
                  onOpenChange(false);
                }}
                planId={selected || 'plus'}
                couponCode={''}
                displayAmount={quote?.quoted_amount || 0}
                description={`Prorrata de upgrade de plano`}
                transactionType={'proration'}
                prorationMeta={{ from_plan_id: currentPlanId, to_plan_id: selected, quoted_amount: quote?.quoted_amount, cycle_days: quote?.cycle_days, days_left: quote?.days_left, delta: quote?.delta, from_price: quote?.from_price, to_price: quote?.to_price, effective_date: quote?.effective_date }}
              />
            ) : (
              <ProrationCardPayment
                toPlanId={selected || 'plus'}
                amount={quote?.quoted_amount || 0}
                onSuccess={async ()=>{
                  const { data: { session } } = await supabase.auth.getSession();
                  const userId = session?.user?.id;
                  await supabase.functions.invoke('subscription_changes', {
                    body: { action: 'apply_upgrade_now', tenant_id: tenantId, from_plan_id: currentPlanId, to_plan_id: selected, user_id: userId, already_paid: true }
                  });
                  onOpenChange(false);
                }}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
